import { useEffect, useRef, useState } from 'react';
import { C } from '@ridgewill/brand';
import type { JobIndex, JobKey, Steps } from '../lib/fleet-api.js';
import { FIRST_DIRECTION, sameJob, searchJobs, sortJobs, type JobSort, type SortDirection } from '../lib/fleet-jobs.js';
import { seconds } from '../lib/fleet-view.js';
import { useAsync } from '../lib/useAsync.js';
import { count, numeric } from '../lib/format.js';
import { AsyncBoundary, Button, EmptyState, inputStyle, Panel, Table, Td, When } from '../components/ui.js';
import { Modal } from '../components/Modal.js';

/**
 * Job analysis: where one job's time goes, step by step and box by box.
 * RidgeWill/org-admin#111.
 *
 * These panels sat under the run history and always showed one job fleet-api
 * picked on its own (the slowest test job), with nothing on the screen saying
 * so. Jon, 13 Sep, asked for them as their own section with a picker. So the
 * tab names its job, says when it was chosen automatically, and a picker lists
 * every job that ran with enough to choose by.
 *
 * ── THE JOB IS IN THE ADDRESS ────────────────────────────────────────
 *
 * `#/ci-fleet/jobs?repo=&workflow=&job=`, like every other section here, so a
 * link to "auth's test job, analysed" opens exactly that. No job named means
 * fleet-api's own default.
 */

/** Runner kinds, in the order the rest of the CI fleet screen uses. */
const KINDS = [
  { kind: 'fleet', label: 'Your runners' },
  { kind: 'blacksmith', label: 'Blacksmith' },
  { kind: 'github', label: 'GitHub-hosted' },
] as const;

const DAYS = 30;

export function JobAnalysis({
  job,
  onChoose,
  loadSteps,
  loadJobs,
}: {
  job: JobKey | undefined;
  onChoose: (job: JobKey) => void;
  loadSteps: (days: number, job?: JobKey) => Promise<Steps>;
  loadJobs: (days: number) => Promise<JobIndex>;
}) {
  const steps = useAsync(() => loadSteps(DAYS, job), [job?.repo, job?.workflow, job?.job_name]);
  const [picking, setPicking] = useState(false);
  const shown = steps.state.status === 'ready' ? steps.state.data.job : null;

  return (
    <div style={s.column} className="rw-fade">
      <Panel title="Job analysis" action={<Button onClick={() => setPicking(true)}>Choose job</Button>}>
        <div style={s.current}>
          {shown ? (
            <>
              <div style={{ ...s.currentName, ...s.clamp }} title={`${shown.workflow} · ${shown.job_name}`}>
                {shown.workflow} · {shown.job_name}
              </div>
              <div style={s.muted}>
                {shown.repo}
                {job ? '' : '. Chosen automatically: the slowest test job in the last 30 days.'}
              </div>
            </>
          ) : steps.state.status === 'loading' ? (
            <div style={s.muted}>Loading…</div>
          ) : steps.state.status === 'ready' ? (
            <div style={s.muted}>No job with step timings in the last 30 days. Choose one to see how it runs on each box.</div>
          ) : null}
        </div>
      </Panel>

      <AsyncBoundary state={steps.state}>{(d) => (d.job ? <Breakdown steps={d} /> : null)}</AsyncBoundary>

      {picking ? (
        <JobPicker
          load={loadJobs}
          current={shown}
          onClose={() => setPicking(false)}
          onPick={(picked) => {
            setPicking(false);
            onChoose(picked);
          }}
        />
      ) : null}
    </div>
  );
}

function Breakdown({ steps }: { steps: Steps }) {
  const kinds = KINDS.filter((k) => steps.by_kind.some((b) => b.kind === k.kind));
  return (
    <>
      <Panel title="Where the time goes">
        {steps.steps.length === 0 ? (
          <EmptyState title="No step timings for this job" suggestion="Step timings are recorded for test jobs only." />
        ) : (
          <Table columns={['Step', ...kinds.map((k) => `${k.label} median`)]}>
            {steps.steps.map((step) => (
              <tr key={step.name}>
                <Td>{step.name}</Td>
                {kinds.map((k) => (
                  <Td key={k.kind}>
                    <span style={numeric}>{seconds(step.by_kind.find((b) => b.kind === k.kind)?.median_s)}</span>
                  </Td>
                ))}
              </tr>
            ))}
          </Table>
        )}
      </Panel>
      <Panel title="The same job on each box">
        {steps.boxes.length === 0 ? (
          <EmptyState title="This job has not passed on your runners in the last 30 days" />
        ) : (
          <Table columns={['Runner', 'Runs', 'Median', 'p90']}>
            {steps.boxes.map((b) => (
              <tr key={b.runner_name}>
                <Td mono>{b.runner_name}</Td>
                <Td>
                  <span style={numeric}>{count(b.runs)}</span>
                </Td>
                <Td>
                  <span style={numeric}>{seconds(b.median_s)}</span>
                </Td>
                <Td>
                  <span style={numeric}>{seconds(b.p90_s)}</span>
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>
    </>
  );
}

// ── THE PICKER ───────────────────────────────────────────────────────

/**
 * Every job that ran in the last 30 days, searchable and sortable.
 *
 * Its own component rather than `PickList`: that one is a checklist of a few
 * options, and its header says a searchable picker over many should be a
 * separate component rather than a mode.
 */
function JobPicker({
  load,
  current,
  onPick,
  onClose,
}: {
  load: (days: number) => Promise<JobIndex>;
  current: JobKey | null;
  onPick: (job: JobKey) => void;
  onClose: () => void;
}) {
  const { state } = useAsync(() => load(DAYS));
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<{ key: JobSort; direction: SortDirection }>({ key: 'last', direction: 'desc' });
  const search = useRef<HTMLInputElement>(null);

  // Runs after the Modal's own effect, which focuses its close button: a parent's
  // effects run after its children's, and the search is what somebody came to use.
  useEffect(() => search.current?.focus(), []);

  const rows = state.status === 'ready' ? sortJobs(searchJobs(state.data.jobs, query), sort.key, sort.direction) : [];

  const header = (key: JobSort, label: string) => {
    const on = sort.key === key;
    return (
      <button
        type="button"
        onClick={() =>
          setSort(on ? { key, direction: sort.direction === 'asc' ? 'desc' : 'asc' } : { key, direction: FIRST_DIRECTION[key] })
        }
        aria-label={`Sort by ${label}`}
        style={{ ...s.sortButton, color: on ? C.text : 'inherit' }}
      >
        {label}
        <span aria-hidden style={{ opacity: on ? 1 : 0 }}>
          {sort.direction === 'asc' ? ' ↑' : ' ↓'}
        </span>
      </button>
    );
  };

  return (
    <Modal title="Choose a job" subtitle="Every job that ran in the last 30 days." onClose={onClose} width={860}>
      <input
        ref={search}
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search by repo, workflow or job"
        aria-label="Search jobs"
        style={inputStyle}
      />
      {state.status === 'loading' ? (
        <p style={s.muted}>Loading jobs…</p>
      ) : state.status === 'failed' ? (
        <p style={{ ...s.muted, color: C.dangerText }}>{state.message}</p>
      ) : rows.length === 0 ? (
        <EmptyState title="No jobs match" suggestion="Try fewer words." />
      ) : (
        <div style={s.pickerFrame}>
          <Table
            columns={[header('name', 'Job'), header('repo', 'Repo'), header('runs', 'Runs'), header('median', 'Median run time'), header('last', 'Last run')]}
          >
            {rows.map((j) => {
              const on = sameJob(j, current);
              return (
                <tr
                  key={JSON.stringify([j.repo, j.workflow, j.job_name])}
                  className="rw-rowhover"
                  onClick={() => onPick(j)}
                  style={{ cursor: 'pointer', background: on ? C.blueTint : undefined }}
                >
                  <Td>
                    {/* A real button, so the list is usable from the keyboard; the row click is the shortcut. */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onPick(j);
                      }}
                      style={{ ...s.jobButton, ...s.clamp }}
                      title={`${j.workflow} · ${j.job_name}`}
                      aria-current={on ? 'true' : undefined}
                    >
                      {j.workflow} · {j.job_name}
                    </button>
                    {on ? <div style={s.tag}>Showing now</div> : !j.has_steps ? <div style={s.tag}>No step timings</div> : null}
                  </Td>
                  <Td>{j.repo}</Td>
                  <Td>
                    <span style={numeric}>{count(j.total)}</span>
                    {j.failed > 0 ? <span style={{ ...numeric, color: C.dangerText }}> · {count(j.failed)} failed</span> : null}
                  </Td>
                  <Td>
                    <span style={numeric}>{seconds(j.median_s)}</span>
                  </Td>
                  <Td>
                    <When iso={j.last_run_at} />
                  </Td>
                </tr>
              );
            })}
          </Table>
        </div>
      )}
    </Modal>
  );
}

const s = {
  column: { display: 'grid', gap: '1rem', gridTemplateColumns: 'minmax(0, 1fr)' },
  current: { padding: '.9rem 1.2rem', display: 'grid', gap: '.2rem' },
  currentName: { fontSize: '1rem', fontWeight: 650, color: C.text },
  muted: { margin: 0, fontSize: '.82rem', color: C.muted, lineHeight: 1.5 },
  sortButton: {
    font: 'inherit',
    textTransform: 'inherit' as const,
    letterSpacing: 'inherit',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
  },
  pickerFrame: { border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' },
  jobButton: {
    font: 'inherit',
    fontWeight: 600,
    color: C.text,
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    textAlign: 'left' as const,
    overflowWrap: 'break-word' as const,
  },
  tag: { fontSize: '.72rem', color: C.muted, marginTop: '.1rem' },
  /**
   * Two lines at most, the full name in the tooltip. Dependabot names its
   * workflow after every package in the update, several hundred characters
   * ("npm_and_yarn in / for @expo/ui, @tanstack/react-query, ..."), which
   * turned one picker row into a paragraph.
   */
  clamp: {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical' as const,
    overflow: 'hidden',
    maxWidth: 420,
  },
};
